import ClassifierImplementation, {
    ClassifierImplementationType,
} from "models/ClassifierImplementationModel";
import { LLMModel } from "./LLMModel";
import { ILLMExecutorStep } from "./LogicChaining/LLMExecutorStep";
import { LLMResultDefinition } from "./LLMResultDefinition";
import { Condition } from "./LogicChaining/Conditions/Condition";
import { LLMClassifierPurpose } from "./LLMClassifierPurpose";
import type { LLMClassifierApprovalEventSummary } from "./LLMClassifierApproval";
import { ApprovalStatus } from "./LLMClassifierApproval";

/** A role grant as a version declared it. */
export interface LLMClassifierVersionRoleAccess {
    roleId: string;
    accessType: number;
}

/** An output as a version declared it. `id` is the identity, so a changed `result` is a rename. */
export interface LLMClassifierVersionOutput {
    id: string;
    result: string;
}

/** A Volume Control row as a version declared it. */
export interface LLMClassifierVersionQuota {
    modelQuota: number;
    quotaFrequency: number;
    /** Empty means the quota applies everywhere, not nowhere. */
    organizationStructureMemberIds: string[];
}

/** What a version declared for state whose live values live on the classifier and its related tables. */
export interface LLMClassifierVersionParent {
    classifierId: string;
    /** The parent's name as of this version's save, so a since-renamed parent still reads the way it did. */
    name?: string;
}

export interface LLMClassifierVersionSettings {
    name?: string;
    description?: string;
    classifierGroupId?: string;
    /** Resolved server-side, so a since-renamed group still reads as a name. */
    classifierGroupName?: string;
    isInternal: boolean;
    rolesAccessControls: LLMClassifierVersionRoleAccess[];
    /** Aligned with `rolesAccessControls`; null where the role no longer exists. */
    roleNames: (string | null)[];
    outputs: LLMClassifierVersionOutput[];
    modelComputeQuotas: LLMClassifierVersionQuota[];
    /** Names for hierarchy ids in `modelComputeQuotas`, resolved server-side without app-side filters. */
    hierarchyNames?: Record<string, string>;
}

interface LLMClassifierImplementationBase extends ClassifierImplementation {
    implementationType: ClassifierImplementationType.LLM;
    llmModelId: string;
    llmModel: LLMModel;
    /** Sent instead of `llmModel` by the versions endpoint. */
    llmModelName?: string;
    /** Resolved from `createdBy`; absent if no user matched. */
    createdByName?: string;
    llmTargetSpeaker?: string;
    llmPurpose: LLMClassifierPurpose;
    llmResultDefinitions?: LLMResultDefinition[];
    /** Sent by the versions endpoint. Absent on versions saved before settings were recorded. */
    settings?: LLMClassifierVersionSettings;
    /** Direct parents this version declared. Always present, unlike `settings`. */
    parentModels?: LLMClassifierVersionParent[];
    /** The version this one was saved from. Sent on save; echoed back by the versions endpoint. */
    branchedFromImplementationId?: string;
    /** Null/absent on legacy versions and displayed as Draft. */
    approvalStatus?: ApprovalStatus;
    /** Latest request and latest state-changing event, supplied by the versions endpoint. */
    approvalRequest?: LLMClassifierApprovalEventSummary;
    latestApprovalEvent?: LLMClassifierApprovalEventSummary;
}

export interface LLMClassifierImplementationPromptOnly
    extends LLMClassifierImplementationBase {
    type: LLMClassifierImplementationConfigurationType.PromptOnly;
    configuration: LLMClassifierImplementationConfigurationPromptOnly;
}

export interface LLMClassifierImplementationLogicChaining
    extends LLMClassifierImplementationBase {
    type: LLMClassifierImplementationConfigurationType.LogicChaining;
    configuration: LLMClassifierImplementationConfigurationLogicChain;
}

export type LLMClassifierImplementation =
    | LLMClassifierImplementationPromptOnly
    | LLMClassifierImplementationLogicChaining;

export enum LLMClassifierImplementationConfigurationType {
    PromptOnly = 1,
    LogicChaining = 2,
}

export interface LLMClassifierImplementationConfigurationBase {
    executeConditions: Condition[];
    /** Written for future ID-based readers; the Platform editor currently reads executeConditions. */
    executeConditionsV2?: Condition[] | null;
}

export interface LLMClassifierImplementationConfigurationPromptOnly
    extends LLMClassifierImplementationConfigurationBase {
    promptTemplate: string;
    questionText: string;
    questionDescription: string;
}

export interface LLMClassifierImplementationConfigurationLogicChain
    extends LLMClassifierImplementationConfigurationBase {
    steps: ILLMExecutorStep[];
}

export interface LLMChildClassifierImplementation
    extends ClassifierImplementation {
    implementationType: ClassifierImplementationType.LLMChild;
    llmModelId: string;
    llmModel: LLMModel;
}
